import { useMemo, useState } from "react"

import type { CompanyDirectoryEntry, CompanyDisplayRole } from "../utils/segment-connections"

import { Building2, ChevronRight, Search, Star } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { cn } from "@/lib/utils"

import { FavouritesFilterToggle } from "./favourites-filter-toggle"
import { companyDisplayRole } from "../utils/segment-connections"

type CompaniesIndexProps = {
  directory: CompanyDirectoryEntry[]
  onOpenCompany: (name: string, segmentId: string) => void
  /** Per-user favourites across segments (#2072); omit to hide the stars. */
  isFavourite?: (companyName: string) => boolean
  onToggleFavourite?: (entry: CompanyDirectoryEntry) => void
  /** Header Market and/or Owner chips are selected. Defaults false. */
  headerFiltersActive?: boolean
}

type RoleFilter = "all" | CompanyDisplayRole

/** Mirrors the map legend buckets (#2072): Both = customer in one segment,
 * competitor in another. */
const ROLE_FILTERS: Array<{ id: RoleFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "customer", label: "Customers" },
  { id: "competitor", label: "Competitors" },
  { id: "both", label: "Both" },
]

export function CompaniesIndex({
  directory,
  headerFiltersActive = false,
  isFavourite,
  onOpenCompany,
  onToggleFavourite,
}: CompaniesIndexProps) {
  const [search, setSearch] = useState("")
  const [role, setRole] = useState<RoleFilter>("all")
  const [favouritesOnly, setFavouritesOnly] = useState(false)

  const results = useMemo(() => {
    const query = search.trim().toLowerCase()
    return directory.filter((entry) => {
      // "customer"/"competitor" include dual-role companies; "both" is exact.
      const display = companyDisplayRole(entry.roles)
      const matchesRole =
        role === "all" || (role === "both" ? display === "both" : entry.roles.includes(role))
      const matchesQuery = !query || entry.name.toLowerCase().includes(query)
      const matchesFavourite = !favouritesOnly || (isFavourite?.(entry.name) ?? false)
      return matchesRole && matchesQuery && matchesFavourite
    })
  }, [directory, favouritesOnly, isFavourite, role, search])

  const hasActiveFilters = Boolean(search.trim()) || role !== "all" || favouritesOnly
  let emptyDescription = "Try a different search, role or favourites filter."
  if (directory.length === 0 && headerFiltersActive) {
    emptyDescription =
      "No companies match the Market and Owner filters. Adjust those chips in the header."
  } else if (directory.length === 0) {
    emptyDescription = "Companies appear here once segments list customers or competitors."
  }

  return (
    <section className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <input
            className="border-input bg-background focus-visible:border-primary focus-visible:ring-primary/15 h-10 w-full rounded-md border pr-3 pl-9 text-sm shadow-sm transition-[box-shadow,border-color] duration-200 focus-visible:ring-[3px] focus-visible:outline-none"
            placeholder="Search companies..."
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <div className="bg-muted text-muted-foreground flex rounded-md p-0.5 text-xs font-medium shadow-inner">
          {ROLE_FILTERS.map((option) => (
            <button
              key={option.id}
              aria-pressed={role === option.id}
              type="button"
              className={cn(
                "btn-spring focus-visible:ring-ring inline-flex h-8 items-center rounded px-3 transition focus-visible:ring-2 focus-visible:outline-none",
                role === option.id
                  ? "bg-background text-foreground ring-border/70 shadow-sm ring-1"
                  : "hover:text-foreground"
              )}
              onClick={() => setRole(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
        {isFavourite ? (
          <FavouritesFilterToggle
            active={favouritesOnly}
            label="Favourites only"
            onToggle={() => setFavouritesOnly((current) => !current)}
          />
        ) : null}
        <span aria-live="polite" className="text-muted-foreground ml-auto text-xs">
          {results.length === directory.length
            ? `${directory.length} companies`
            : `${results.length} of ${directory.length} companies`}
        </span>
      </div>

      {results.length === 0 ? (
        <EmptyState
          className="border-border bg-card rounded-xl border p-12"
          description={emptyDescription}
          icon={Building2}
          title="No companies found"
          primaryAction={
            directory.length > 0 && hasActiveFilters ? (
              <Button
                variant="outline"
                onClick={() => {
                  setSearch("")
                  setRole("all")
                  setFavouritesOnly(false)
                }}
              >
                Clear filters
              </Button>
            ) : undefined
          }
        />
      ) : (
        <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {results.map((entry) => (
            <li key={entry.name}>
              <CompanyRow
                entry={entry}
                favourited={isFavourite?.(entry.name) ?? false}
                onOpen={onOpenCompany}
                onToggleFavourite={onToggleFavourite}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function CompanyRow({
  entry,
  favourited,
  onOpen,
  onToggleFavourite,
}: {
  entry: CompanyDirectoryEntry
  favourited: boolean
  onOpen: (name: string, segmentId: string) => void
  onToggleFavourite?: (entry: CompanyDirectoryEntry) => void
}) {
  return (
    <div
      className="card-interactive border-border bg-card group relative flex w-full items-start gap-3 rounded-lg border p-3 text-left shadow-sm transition"
      role="presentation"
      // Mouse convenience for the whole row; keyboard users activate the inner button.
      onClick={() => onOpen(entry.name, entry.segments[0].id)}
    >
      <button
        className="focus-visible:ring-ring flex min-w-0 flex-1 items-start gap-3 rounded-sm text-left focus-visible:ring-2 focus-visible:outline-none"
        type="button"
      >
        <span className="bg-primary/10 text-primary flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-bold">
          {initials(entry.name)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <span className="text-foreground truncate text-sm font-semibold">{entry.name}</span>
            <RoleBadge role={companyDisplayRole(entry.roles)} />
          </span>
          <span className="text-muted-foreground mt-1 line-clamp-2 block text-xs">
            {entry.segments.map((segment) => segment.name).join(" · ")}
          </span>
        </span>
      </button>
      {onToggleFavourite ? (
        <button
          aria-label={favourited ? `Unfavourite ${entry.name}` : `Favourite ${entry.name}`}
          aria-pressed={favourited}
          type="button"
          className={cn(
            "focus-visible:ring-ring mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-md transition focus-visible:ring-2 focus-visible:outline-none",
            favourited
              ? "text-warning-ink"
              : "text-muted-foreground/50 hover:bg-accent hover:text-warning-ink"
          )}
          onClick={(event) => {
            event.stopPropagation()
            onToggleFavourite(entry)
          }}
        >
          <Star className={cn("size-4", favourited && "fill-current")} />
        </button>
      ) : null}
      <ChevronRight className="text-muted-foreground group-hover:text-foreground mt-1 size-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
    </div>
  )
}

/** One badge per company, matching the map legend buckets — dual-role companies
 * show a single amber BOTH instead of two competing chips (#2072). */
function RoleBadge({ role }: { role: CompanyDisplayRole }) {
  const styles: Record<CompanyDisplayRole, string> = {
    customer: "bg-primary/10 text-primary",
    competitor: "bg-muted text-muted-foreground",
    both: "bg-warning/15 text-warning-foreground",
  }
  return (
    <span
      className={cn("rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase", styles[role])}
    >
      {role}
    </span>
  )
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((word) => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase()
}
